/**
 * Logística de viagem no texto do pedido: aterragem/chegada à cidade, partida
 * e bagagem. Puro e determinístico — sem IO, sem IA, sem Maps.
 *
 * Regras vinculativas do plano "Qualidade temporal dos dias":
 * - "aterro às 14h no aeroporto, com hotel escolhido" → desembarque, bagagem,
 *   transferência e check-in ANTES da primeira visita;
 * - "estou na cidade às 14h", sem hotel → nenhum hotel inventado; a rota
 *   começa às 14h;
 * - "aterro às 14h no aeroporto", sem hotel → nenhum check-in inventado;
 *   pergunta onde deixar a bagagem ou permite "Criar sem logística".
 *
 * O trace nunca recebe dados pessoais: apenas flags técnicas
 * (arrivalProvided, arrivalKind, airportResolved…).
 */

export type ArrivalKind = "airport" | "city" | "hotel";

export type ParsedArrival = {
  kind: ArrivalKind;
  /** Hora local de referência, em minutos desde a meia-noite. */
  timeMinutes?: number;
  /** Nome do aeroporto quando explicitamente escrito ("Heathrow"). */
  airportName?: string;
  /** Alias estável do contrato estruturado (código IATA ou nome). */
  airportCodeOrName?: string;
};

export type ParsedDeparture = {
  /** Hora local da partida (voo/comboio), em minutos desde a meia-noite. */
  timeMinutes?: number;
  /** Verdadeiro quando a partida é claramente de aeroporto. */
  isAirport: boolean;
  airportName?: string;
  /** A partida foi declarada para o último dia da viagem. */
  lastDay?: boolean;
  /**
   * "vou embora às 17h": hora declarada mas tipo de saída por esclarecer.
   * A hora nunca é ignorada — a rota fica limitada até haver resposta.
   */
  kindUnknown?: boolean;
};


export type TravelLogistics = {
  arrival?: ParsedArrival;
  departure?: ParsedDeparture;
  /** Bagagem explicitamente referida no pedido ("deixo a bagagem no hotel"). */
  baggageStated?: boolean;
  /** Hotel/check-in explicitamente referido como ponto de chegada. */
  hotelStated?: boolean;
  /** Existe um hotel realmente escolhido pela pessoa (com identidade). */
  hotelSelected?: boolean;

  /** A pessoa escolheu "Criar sem logística": sem perguntas, margens conservadoras. */
  skipLogistics?: boolean;
};

/** Desembarque + bagagem (margem conservadora e editável). */
export const LANDING_BAGGAGE_MIN = 60;
/** Transferência aeroporto → cidade, sem indicação em contrário. */
export const AIRPORT_TRANSFER_DEFAULT_MIN = 60;
/** Check-in / arrumação no hotel. */
export const HOTEL_CHECKIN_MIN = 30;
/** Chegada ao aeroporto antes de um voo internacional (margem conservadora). */
export const AIRPORT_DEPARTURE_MARGIN_MIN = 150;
/** Transferência cidade → aeroporto, sem indicação em contrário. */
export const AIRPORT_TRANSFER_DEPARTURE_DEFAULT_MIN = 60;
/** Partida genérica sem aeroporto (comboio/autocarro): margem menor. */
export const GENERIC_DEPARTURE_MARGIN_MIN = 45;

/** Frase-marcador da opção rápida "Criar sem logística". */
export const LOGISTICS_SKIP_MARKER = "sem logística de bagagem";

/**
 * Normalização SEM alterar o comprimento do texto: cada carácter é reduzido ao
 * seu equivalente sem acento. Isto é obrigatório porque os índices calculados
 * sobre o texto normalizado são usados para recortar o texto original (nomes
 * de aeroporto preservam maiúsculas).
 */
const fold = (value: string) =>
  [...((value ?? "").replace(/[’‘`]/gu, "'"))]
    .map((c) => c.normalize("NFD").replace(/[\u0300-\u036f]/gu, "") || c)
    .join("")
    .toLowerCase();

type TimeRule = { re: RegExp; meridiem?: boolean };

const TIME_PATTERNS: TimeRule[] = [
  // "4pm", "4:30 pm", "11 am" — tem de vir primeiro ("at 4pm" ≠ 04:00)
  { re: /\b(\d{1,2})(?:[:.](\d{2}))?\s*(a\.?m\.?|p\.?m\.?)\b/iu, meridiem: true },
  // "16 Uhr", "16:30 Uhr"
  { re: /\b(\d{1,2})(?:[:.h](\d{2}))?\s*uhr\b/iu },
  // "às 14", "at 14", "a las 14", "um 14", "alle 14", "a 14"
  { re: /\b(?:[àa]s?|at|a\s+las|a\s+la|um|gegen|alle|all'|vers)\s+(\d{1,2})(?:[:.h](\d{2}))?(?![\d])/iu },
  // "14:30", "14h30", "9.30"
  { re: /\b(\d{1,2})[:h.](\d{2})(?![\d])/u },
  // "14h", "9 horas", "22 hrs", "9 heures", "9 ore", "9 Stunden"
  { re: /\b(\d{1,2})\s*(?:h|horas?|hours?|hrs?|heures?|ore|stunden?)\b/iu },
];

function extractTime(slice: string): { minutes: number } | null {
  for (const { re, meridiem } of TIME_PATTERNS) {
    const match = slice.match(re);
    if (!match) continue;
    let hour = Number.parseInt(match[1], 10);
    const minute = match[2] ? Number.parseInt(match[2], 10) : 0;
    if (meridiem) {
      const isPm = /p/i.test(match[3] ?? "");
      if (hour < 1 || hour > 12) continue;
      hour = isPm ? (hour === 12 ? 12 : hour + 12) : hour === 12 ? 0 : hour;
    }
    if (!Number.isFinite(hour) || hour < 0 || hour > 23) continue;
    if (!Number.isFinite(minute) || minute < 0 || minute > 59) continue;
    return { minutes: hour * 60 + minute };
  }
  return null;
}

function timeNear(text: string, from: number, span: number): { minutes: number } | null {
  return extractTime(text.slice(from, from + span));
}

const AIRPORT_WORD_RE =
  /\b(aterro|aterragem|aterrissagem|desembarque|desembarco|pousamos|pouso|aviao|voo|voos|vuelo|volo|avion|aereo|flugzeug|flug|fluge|flight|flights|landing|lands|airport|aeroporto|aeropuerto|aeroport|flughafen|terminal)\b/;

/**
 * Verbos de aterragem em PT/EN/ES/FR/IT/DE. Uma palavra isolada nunca chega:
 * o resultado só vira "airport" quando houver hora e sinal de voo/aeroporto.
 */
const LANDING_RE =
  /\b(aterro|aterragem|aterrissagem|desembarque|desembarco|pousamos|pouso|land(?:ing|s|ed)?|lande|landen|atterr\w+|aterriz\w+|deixar\s+o\s+aviao|chego\s+(?:ao|au|al|to\s+the)?\s*aeroporto|vou\s+chegar\s+ao\s+aeroporto|arriv\w*\s+(?:at|in)\s+(?:the\s+)?(?:airport|aeroporto)|llego\s+al\s+aeropuerto|arrivo\s+(?:in|all')\s*aeroporto|komme\s+(?:am|im)\s+flughafen\s+an)\b/;

/**
 * "chego às 15h de avião" é aterragem, nunca chegada à cidade: o modo de
 * transporte declarado obriga a resolver o aeroporto antes de gerar.
 */
const BY_PLANE_RE =
  /\b(de|em)\s+aviao\b|\bby\s+(?:plane|air)\b|\bfly(?:ing)?\s+(?:into|to)\b|\ben\s+avion\b|\bpor\s+avion\b|\ben\s+avion\b|\bin\s+aereo\b|\bcon\s+l'?\s*aereo\b|\bmit\s+dem\s+flugzeug\b|\bfliege\b|\bper\s+via\s+aerea\b|\bvoo\s+de\s+chegada\b|\bde\s+voo\b/;


const HOTEL_ARRIVAL_RE =
  /\b(check-?in|no\s+hotel|at\s+the\s+hotel|ao\s+hotel|chego\s+ao\s+hotel|estou\s+no\s+hotel)\b/;

/**
 * Sinais de chegada em PT/EN/ES/FR/IT/DE. Só produzem logística quando existe
 * também uma hora reconhecida perto da expressão.
 */
const CITY_ARRIVAL_RE =
  /\b(estou\s+(?:em|na|no|in)\b|chego\w*\b|chegamos\b|chegarei\b|estarei\s+(?:em|na|no|in)\b|llego\w*\b|llegando\b|llegada\b|arrive\b|arrives\b|arriving\b|arrival\b|arriv\w*\s+in\s+(?:the\s+)?city|j'\s*arrive\b|arrivee\b|arrivo\b|arrivero\b|arrivando\b|ankomme\b|ankunft\b|komme\s+an\b|komme\s+um\b)/;

const BAGGAGE_RE =
  /\b(bagagem|malas?|mala\s+de\s+mao|luggage|baggage|suitcase|maleta|bagagli|gepack|equipaje|carry-?on|backpack)\b/;

const HOTEL_WORD_RE = /\b(hotel|alojamento|hostel|airbnb|apartamento|accommodation)\b/;

const DEPARTURE_RE =
  /\b(voo\s+de\s+(?:regresso|volta|retorno)|meu\s+voo|tenho\s+(?:um\s+)?voo|voo\s+[àa]s|apanho\s+(?:o\s+)?voo|flight\s+(?:is\s+)?(?:at|departs|leaves|from)|my\s+flight|departure|departing|departs?\s+(?:at|from)|depart\s+(?:a|de|vers|le)|partida|partenza|parto|vol\s+de\s+retour|mon\s+vol|je\s+pars|il\s+mio\s+volo|volo\s+(?:di\s+ritorno|alle)|ruckflug|abflug|mein\s+flug|fliege\s+(?:zuruck|um)|salgo|salida|vuelo\s+(?:de\s+(?:regreso|vuelta)|a\s+las)|mi\s+vuelo|regresso\s+de\s+aviao)\b/;

/**
 * Saída declarada sem tipo claro ("vou embora às 17h", "saio às 17h").
 * A hora é vinculativa; o tipo (voo ou saída da cidade) é perguntado.
 */
const AMBIGUOUS_DEPARTURE_RE =
  /\b(vou\s+embora|vamos\s+embora|vou-me\s+embora|venho\s+embora|vimos\s+embora|venho-me\s+embora|regresso\s+[àa]s|volto\s+[àa]s|saio\s+da\s+cidade|saio|saimos|deixo\s+a\s+cidade|deixamos\s+a\s+cidade|i\s+leave|we\s+leave|leaving\s+at|check\s*-?\s*out\s+e\s+saio)\b/;

/**
 * Saída por terra/mar explícita (comboio, autocarro, barco, carro). O tipo é
 * conhecido: não é voo e não há pergunta de clarificação.
 */
const GROUND_DEPARTURE_RE =
  /\b(comboio|combóio|tren|treno|train|zug|autocarro|onibus|ônibus|bus|autobus|coach|barco|ferry|ferri|carro|car|conduzir|drive|estacao|estación|gare|bahnhof|stazione|station)\b/;

/** Marcador determinístico da resposta "não é voo" (saída por terra). */
const NO_FLIGHT_RE = /\bsaida\s+por\s+terra\b/;


/** "no último dia", "last day", "no fim da viagem" → partida no último dia. */
const LAST_DAY_RE =
  /\b(ultimo\s+dia|last\s+day|dernier\s+jour|ultimo\s+giorno|letzte[rn]\s+tag|ultimo\s+dia\s+da\s+viagem|no\s+fim\s+da\s+viagem)\b/;

const SKIP_RE = /\bsem\s+log[íi]?stica|no\s+luggage\s+logistics|skip\s+(?:the\s+)?logistics\b/;

function firstTime(
  text: string,
  re: RegExp,
  before = 24,
  after = 48,
  afterOnly = false,
): { minutes: number; at: number } | null {
  const match = re.exec(text);
  if (!match || match.index < 0) return null;
  if (afterOnly) {
    // "voo de regresso às 20h": a hora relevante vem DEPOIS da palavra-chave,
    // nunca antes (evita apanhar a hora de chegada referida atrás).
    const from = match.index + match[0].length;
    const time = timeNear(text, from, after);
    return time ? { minutes: time.minutes, at: match.index } : null;
  }
  const from = Math.max(0, match.index - before);
  const time = timeNear(text, from, before + match[0].length + after);
  return time ? { minutes: time.minutes, at: match.index } : null;
}

/** Nome de aeroporto escrito junto à palavra-chave ("aeroporto de Heathrow"). */
const AIRPORT_NAME_RE =
  /\b(?:aeroporto|airport|aéroport|aeroport|flughafen|aeropuerto)\s+(?:de|do|da|of|d'|di|von)?\s*([A-Z][\p{L}'’-]{2,30}(?:\s+[A-Z][\p{L}'’-]{2,30})?)/u;

/** Código IATA imediatamente antes da palavra-chave ("JFK airport", "JFK Flughafen"). */
const AIRPORT_CODE_BEFORE_RE =
  /\b([A-Z]{3})\s+(?:aeroporto|airport|aéroport|aeroport|flughafen|aeropuerto)\b/u;

/** Código IATA depois de preposição de origem/destino ("from JFK", "para JFK"). */
const AIRPORT_CODE_LOOSE_RE =
  /\b(?:from|de|do|da|para|to|desde|depuis|von|ab|da|at|in|no|em)\s+([A-Z]{3})\b(?![\p{L}])/u;

function airportName(text: string): string | undefined {
  const match = text.match(AIRPORT_NAME_RE);
  if (match?.[1]) return match[1].trim();
  const codeBefore = text.match(AIRPORT_CODE_BEFORE_RE);
  if (codeBefore?.[1]) return codeBefore[1];
  // Nomes próprios de aeroportos conhecidos sem a palavra "aeroporto".
  const known = text.match(/\b(heathrow|gatwick|stansted|luton|london\s+city|charles\s+de\s+gaulle|orly|schiphol|barajas|malpensa|linate|frankfurt|munich|munique|zurich|zurique|geneva|genebra|jfk|newark|o'hare|fiumicino|tegel|brandenburg|el\s+prat)\b/iu);
  if (known?.[1]) return known[1];
  // Só aceitamos um código solto quando o texto fala mesmo de voo/aeroporto.
  if (AIRPORT_WORD_RE.test(fold(text))) {
    const loose = text.match(AIRPORT_CODE_LOOSE_RE);
    if (loose?.[1]) return loose[1];
  }
  return undefined;
}

/** Opções externas ao texto: o que a pessoa já escolheu na interface. */
export type ParseLogisticsOptions = {
  /**
   * Existe um hotel realmente escolhido (com identidade/placeId). Só nesse
   * caso "faço check-in no hotel" pode virar uma chegada do tipo "hotel".
   */
  hotelSelected?: boolean;
};

/**
 * Analisa o texto do pedido e devolve a logística de viagem declarada.
 * Nunca inventa hotel, aeroporto nem check-in: só o que está escrito.
 */
export function parseTravelLogistics(
  prompt: string,
  opts: ParseLogisticsOptions = {},
): TravelLogistics {
  const raw = (prompt ?? "").replace(/\s+/gu, " ").trim();
  const text = fold(raw);
  if (!text) return {};

  const result: TravelLogistics = {};
  if (SKIP_RE.test(text)) result.skipLogistics = true;

  if (BAGGAGE_RE.test(text)) result.baggageStated = true;
  if (HOTEL_WORD_RE.test(text) || HOTEL_ARRIVAL_RE.test(text)) result.hotelStated = true;
  if (opts.hotelSelected) result.hotelSelected = true;

  // Fronteira entre a frase de chegada e a frase de saída: a hora e o
  // aeroporto de uma nunca podem ser lidos a partir da outra.
  const departureMatch = DEPARTURE_RE.exec(text) ?? AMBIGUOUS_DEPARTURE_RE.exec(text);
  const departureAt = departureMatch && departureMatch.index > 0 ? departureMatch.index : -1;
  // O texto normalizado preserva o comprimento, logo os índices servem para
  // recortar também o texto original (necessário para nomes/códigos IATA).
  const prefixText = departureAt > 0 ? text.slice(0, departureAt) : text;
  const departureText = departureAt > 0 ? text.slice(departureAt) : text;
  const departureRaw = departureAt > 0 ? raw.slice(departureAt) : raw;
  // A frase de chegada costuma vir antes da saída, mas nem sempre (respostas
  // de clarificação são anexadas no fim). Só encurtamos o âmbito quando o
  // prefixo contém mesmo um sinal de chegada com hora.
  const prefixHasArrival =
    firstTime(prefixText, LANDING_RE) != null || firstTime(prefixText, CITY_ARRIVAL_RE) != null;
  const arrivalText = prefixHasArrival ? prefixText : text;
  const arrivalRaw = prefixHasArrival && departureAt > 0 ? raw.slice(0, departureAt) : raw;

  // --- Chegada -------------------------------------------------------------
  const landing = firstTime(arrivalText, LANDING_RE);
  const cityTime = firstTime(arrivalText, CITY_ARRIVAL_RE);
  const byPlane = BY_PLANE_RE.test(arrivalText);
  // "chego a Nova Iorque às 16 horas ao aeroporto": a palavra "aeroporto"
  // pertence à frase de chegada, logo é aterragem — nunca chegada à cidade.
  const airportNearArrival =
    cityTime != null &&
    /\b(aeroporto|aeropuerto|aeroport|airport|flughafen|terminal|aviao|avion|aereo|flugzeug|voo|vuelo|volo|flug|flight)\b/.test(
      arrivalText.slice(Math.max(0, cityTime.at - 32), cityTime.at + 96),
    );
  if (landing) {
    result.arrival = {
      kind: "airport",
      timeMinutes: landing.minutes,
      airportName: airportName(arrivalRaw) ?? airportName(raw),
    };
  } else if (cityTime && (byPlane || (airportNearArrival && !HOTEL_ARRIVAL_RE.test(arrivalText)))) {

    // "chego às 15h de avião": aterragem, nunca chegada à cidade.
    result.arrival = {
      kind: "airport",
      timeMinutes: cityTime.minutes,
      airportName: airportName(arrivalRaw) ?? airportName(raw),
    };
  } else if (HOTEL_ARRIVAL_RE.test(arrivalText) && opts.hotelSelected) {
    // Só com hotel escolhido é que "check-in no hotel" define o ponto de
    // chegada; caso contrário seria inventar morada, check-in e bagagem.
    const hotelTime = firstTime(arrivalText, HOTEL_ARRIVAL_RE);
    result.arrival = hotelTime
      ? { kind: "hotel", timeMinutes: hotelTime.minutes }
      : { kind: "hotel" };
  } else if (cityTime) {
    // "chego às 15h a Barcelona" / "chego a Barcelona às 15h": disponibilidade
    // na cidade, mesmo que a frase mencione um hotel ainda não escolhido.
    result.arrival = { kind: "city", timeMinutes: cityTime.minutes };
  } else if (HOTEL_ARRIVAL_RE.test(arrivalText)) {
    const hotelTime = firstTime(arrivalText, HOTEL_ARRIVAL_RE);
    if (hotelTime) result.arrival = { kind: "city", timeMinutes: hotelTime.minutes };
  }

  if (result.arrival && AIRPORT_WORD_RE.test(arrivalText) && result.arrival.kind !== "airport") {
    // "estou no hotel" depois de voo: a chegada continua a ser ao hotel.
    if (!HOTEL_ARRIVAL_RE.test(arrivalText)) result.arrival.airportName = airportName(arrivalRaw);
  }
  if (result.arrival) {
    // Contrato estruturado estável para quem consome a logística.
    result.arrival.airportCodeOrName = result.arrival.airportName;
  }


  // --- Partida -------------------------------------------------------------
  const departure = firstTime(departureText, DEPARTURE_RE, 0, 64, true);
  if (departure) {
    const groundOnly = NO_FLIGHT_RE.test(text);
    const depAirport = airportName(departureRaw) ?? airportName(raw);
    result.departure = {
      timeMinutes: departure.minutes,
      isAirport:
        !groundOnly &&
        (AIRPORT_WORD_RE.test(departureText) ||
          /\b(voo|flight|abflug|ruckflug|regresso)\b/.test(departureText) ||
          // "departing 17:00 from JFK": o código é o próprio sinal de voo,
          // desde que o pedido fale de aeroporto/voo algures.
          (Boolean(depAirport) && AIRPORT_WORD_RE.test(text))),
      airportName: groundOnly ? undefined : depAirport,
      ...(LAST_DAY_RE.test(text) ? { lastDay: true } : {}),
    };
  } else {
    // "no último dia vou embora às 17 horas": a hora é vinculativa mesmo sem
    // saber se é voo. Nunca se ignora silenciosamente a saída.
    const vague = firstTime(text, AMBIGUOUS_DEPARTURE_RE, 0, 64, true);
    if (vague) {
      // "venho embora de comboio às 20h": o meio está escrito, logo o tipo é
      // conhecido (saída por terra) e não há pergunta de clarificação.
      const ground = GROUND_DEPARTURE_RE.test(text.slice(Math.max(0, vague.at - 24), vague.at + 96));
      result.departure = {
        timeMinutes: vague.minutes,
        isAirport: false,
        ...(ground ? {} : { kindUnknown: true }),
        ...(LAST_DAY_RE.test(text) ? { lastDay: true } : {}),
      };
    }
  }



  return result;
}

/**
 * Minutos reservados ANTES da primeira visita no dia 1.
 * - airport: desembarque + bagagem + transferência (+ check-in se o hotel
 *   estiver confirmado — nunca inventado);
 * - hotel: check-in/arrumação;
 * - city: 0 — "estou na cidade às 14h" significa que a rota começa às 14h.
 */
export function arrivalOverheadMinutes(
  arrival: ParsedArrival | undefined,
  opts: { hotelCheckin?: boolean } = {},
): number {
  if (!arrival) return 0;
  if (arrival.kind === "airport") {
    return LANDING_BAGGAGE_MIN + AIRPORT_TRANSFER_DEFAULT_MIN + (opts.hotelCheckin ? HOTEL_CHECKIN_MIN : 0);
  }
  if (arrival.kind === "hotel") return HOTEL_CHECKIN_MIN;
  return 0;
}

/**
 * Hora local (minutos do dia) em que a primeira visita do dia 1 pode começar.
 * Sem chegada declarada devolve `fallbackStartMinutes`.
 */
export function dayOneVisitStartMinutes(
  logistics: TravelLogistics,
  fallbackStartMinutes = 9 * 60,
): number {
  const arrival = logistics.arrival;
  if (!arrival || arrival.timeMinutes == null) return fallbackStartMinutes;
  // "Criar sem logística" remove a logística INVENTADA (desembarque, bagagem,
  // transferência, check-in) — nunca a hora declarada pela pessoa. "Chego às
  // 15h" continua a mandar no início do dia 1.
  if (logistics.skipLogistics) return arrival.timeMinutes;
  const hotelCheckin = arrival.kind === "airport" && !!(logistics.hotelStated || logistics.baggageStated);
  return arrival.timeMinutes + arrivalOverheadMinutes(arrival, { hotelCheckin });
}

/**
 * Hora local (minutos do dia) a partir da qual não há mais visitas no último
 * dia. Sem partida declarada devolve undefined.
 */
export function lastDayVisitEndMinutes(logistics: TravelLogistics): number | undefined {
  const departure = logistics.departure;
  if (!departure || departure.timeMinutes == null) return undefined;
  // "Ainda não sei — criar roteiro sem logística": não há bloco de aeroporto,
  // transferência nem margem inventada, mas a hora declarada continua a ser
  // limite duro — não se agenda nada depois da saída.
  if (logistics.skipLogistics) return Math.max(0, departure.timeMinutes);

  // Saída ambígua ("vou embora às 17h"): guardamos a hora como limite duro,
  // mas NÃO inventamos margem de aeroporto, transferência nem check-out. A
  // agenda simplesmente não pode passar das 17:00 até a pessoa esclarecer.
  if (departure.kindUnknown) return Math.max(0, departure.timeMinutes);

  const lead = departure.isAirport
    ? AIRPORT_DEPARTURE_MARGIN_MIN + AIRPORT_TRANSFER_DEPARTURE_DEFAULT_MIN
    : GENERIC_DEPARTURE_MARGIN_MIN;
  return Math.max(0, departure.timeMinutes - lead);

}

export type LogisticsField = "logistics_arrival" | "logistics_departure";

const ARRIVAL_TIME_QUESTION: Record<string, string> = {
  pt: "A que horas ateras ou chegas à cidade?",
  en: "What time do you land or arrive in the city?",
  es: "¿A qué hora aterrizas o llegas a la ciudad?",
  fr: "À quelle heure atterris-tu ou arrives-tu en ville ?",
  it: "A che ora atterri o arrivi in città?",
  de: "Wann landest du oder kommst du in der Stadt an?",
};

const ARRIVAL_AIRPORT_QUESTION: Record<string, string> = {
  pt: "Em que aeroporto aterras às {time}?",
  en: "Which airport do you land at, at {time}?",
  es: "¿En qué aeropuerto aterrizas a las {time}?",
  fr: "À quel aéroport atterris-tu à {time} ?",
  it: "In quale aeroporto atterri alle {time}?",
  de: "An welchem Flughafen landest du um {time}?",
};

const ARRIVAL_BAGGAGE_QUESTION: Record<string, string> = {
  pt: "Aterras sem hotel indicado — onde deixas a bagagem e onde ficas?",
  en: "You land with no hotel mentioned — where do you leave your luggage and stay?",
  es: "Aterrizas sin hotel indicado — ¿dónde dejas el equipaje y te quedas?",
  fr: "Tu atterris sans hôtel indiqué — où laisses-tu tes bagages et où loges-tu ?",
  it: "Atterri senza hotel indicato — dove lasci i bagagli e dove soggiorni?",
  de: "Du landest ohne genanntes Hotel — wo lässt du dein Gepäck und wo übernachtest du?",
};

const DEPARTURE_TIME_QUESTION: Record<string, string> = {
  pt: "A que horas é a tua partida?",
  en: "What time is your departure?",
  es: "¿A qué hora es tu salida?",
  fr: "À quelle heure est ton départ ?",
  it: "A che ora è la tua partenza?",
  de: "Wann ist deine Abfahrt bzw. Abflug?",
};

const DEPARTURE_AIRPORT_QUESTION: Record<string, string> = {
  pt: "De que aeroporto partes às {time}?",
  en: "Which airport do you depart from at {time}?",
  es: "¿De qué aeropuerto sales a las {time}?",
  fr: "De quel aéroport pars-tu à {time} ?",
  it: "Da quale aeroporto parti alle {time}?",
  de: "Von welchem Flughafen fliegst du um {time} ab?",
};

const DEPARTURE_KIND_QUESTION: Record<string, string> = {
  pt: "A saída às {time} é um voo (aeroporto) ou só saída da cidade?",
  en: "Is your {time} departure a flight (airport) or just leaving the city?",
  es: "¿La salida a las {time} es un vuelo (aeropuerto) o solo salir de la ciudad?",
  fr: "Le départ à {time} est-il un vol (aéroport) ou juste quitter la ville ?",
  it: "La partenza alle {time} è un volo (aeroporto) o solo lasciare la città?",
  de: "Ist die Abreise um {time} ein Flug (Flughafen) oder nur die Stadt verlassen?",
};


function pick(dict: Record<string, string>, language: string): string {
  const key = (language || "pt").slice(0, 2).toLowerCase();
  return dict[key] ?? dict.en ?? dict.pt;
}

export type LogisticsClarification = {
  field: LogisticsField;
  question: string;
};

/** O aeroporto de partida está identificado de forma segura? */
export function departureAirportResolved(logistics: TravelLogistics): boolean {
  const departure = logistics.departure;
  if (!departure || !departure.isAirport) return true;
  return Boolean(departure.airportName && departure.airportName.trim().length >= 3);
}

/** O aeroporto de chegada está identificado de forma segura? */
export function arrivalAirportResolved(logistics: TravelLogistics): boolean {
  const arrival = logistics.arrival;
  if (!arrival || arrival.kind !== "airport") return true;
  return Boolean(arrival.airportName && arrival.airportName.trim().length >= 3);
}

/**
 * Pergunta obrigatória de desambiguação, ou null se a logística está completa
 * (ou a pessoa escolheu "Criar sem logística").
 */
export function logisticsClarification(
  logistics: TravelLogistics,
  language = "pt",
): LogisticsClarification | null {
  if (logistics.skipLogistics) return null;
  const arrival = logistics.arrival;
  if (arrival) {
    if (arrival.timeMinutes == null) {
      return { field: "logistics_arrival", question: pick(ARRIVAL_TIME_QUESTION, language) };
    }
    // Aterragem declarada sem aeroporto identificado: sem aeroporto não há
    // desembarque, bagagem, transferência nem margem — não se gera rota.
    if (!arrivalAirportResolved(logistics)) {
      return {
        field: "logistics_arrival",
        question: pick(ARRIVAL_AIRPORT_QUESTION, language).replace(
          "{time}",
          minutesToHHMM(arrival.timeMinutes),
        ),
      };
    }
    // Aterragem sem hotel e sem bagagem resolvida: pergunta obrigatória.
    if (arrival.kind === "airport" && !logistics.hotelStated && !logistics.baggageStated) {
      return { field: "logistics_arrival", question: pick(ARRIVAL_BAGGAGE_QUESTION, language) };
    }
  }

  const departure = logistics.departure;
  if (departure) {
    if (departure.timeMinutes == null) {
      return { field: "logistics_departure", question: pick(DEPARTURE_TIME_QUESTION, language) };
    }
    // "vou embora às 17h": a hora é vinculativa, mas o tipo é ambíguo.
    if (departure.kindUnknown) {
      return {
        field: "logistics_departure",
        question: pick(DEPARTURE_KIND_QUESTION, language).replace(
          "{time}",
          minutesToHHMM(departure.timeMinutes),
        ),
      };
    }
    // Voo declarado sem aeroporto identificado: sem deslocação segura, não se
    // gera rota. Pergunta local — sem fornecedor e sem crédito.

    if (!departureAirportResolved(logistics)) {
      return {
        field: "logistics_departure",
        question: pick(DEPARTURE_AIRPORT_QUESTION, language).replace(
          "{time}",
          minutesToHHMM(departure.timeMinutes),
        ),
      };
    }
  }
  return null;
}

/** Formata minutos do dia como "HH:MM". */
export function minutesToHHMM(minutes: number): string {
  const clamped = Math.max(0, Math.min(24 * 60 - 1, Math.round(minutes)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
